import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider, keepPreviousData } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

// --- Silence the benign "ResizeObserver loop" browser notice ------------------
// Radix popovers (Select, HoverCard) opening above a Leaflet map can make the
// browser emit "ResizeObserver loop completed with undelivered notifications".
// It is harmless, but the dev overlay promotes it to a fatal runtime error.
// 1) Deflect the loop at its source by deferring observer callbacks to the next
//    animation frame (breaks the synchronous resize→observe→resize cycle).
// 2) Belt-and-braces: swallow the specific error event so no overlay is shown.
if (typeof window !== "undefined" && typeof window.ResizeObserver !== "undefined") {
  const NativeResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends NativeResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          callback(entries, observer);
        });
      });
    }
  };
}

if (typeof window !== "undefined") {
  const RO_LOOP = /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/;
  window.addEventListener(
    "error",
    (e) => {
      if (e && e.message && RO_LOOP.test(e.message)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );
}
// ------------------------------------------------------------------------------

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data only changes on Evergiving sync / HR edits (which bust the server cache),
      // so keep results "fresh" for 5 min to avoid refetching on every tab switch,
      // and hold them in memory for 15 min.
      staleTime: 300_000,
      gcTime: 900_000,
      // v5: keep showing the previous data while a new query resolves (no blank/flash
      // when switching association or navigating) — the old `keepPreviousData: true`
      // flag was silently ignored under react-query v5.
      placeholderData: keepPreviousData,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      // Prevent multi-minute retry storms on slow/failing heavy endpoints:
      // never retry client errors (401/403/404), retry 5xx/network just once
      // with a short bounded backoff.
      retry: (failureCount, error) => {
        const status = error?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);
